<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { EventVault, ProductKey } from "@/models"
import type { IPagination } from "@/types"
import { computed } from "vue"
import { Table } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus, ProductTypes } from "@/models"
import { convertDateTimezone } from "@/utils"
import ActionsMenu from "./ActionsMenu.vue"

interface HostEventsTableProps {
  items: EventVault[]
  pagination?: IPagination
}

interface EventTableRow {
  id?: number
  title: string
  eventDate?: Date
  closeDate?: Date
  status?: EventVaultStatus
  eventGuestMax?: number
  totalGuests?: number
  totalAlbums?: number
  totalPics?: number
  totalVids?: number
  totalMessages?: number
  templateName?: string
  productKey?: ProductKey
  productName?: string
  freemiumMode?: boolean
  upgradeUrl?: string
  actions?: unknown
  timezone?: string
}

const props = defineProps<HostEventsTableProps>()

const emit = defineEmits<{
  paginationNext: []
  paginationPrevious: []
  showLinks: [eventVaultId?: number]
  showExport: [eventVaultId?: number]
  start: [eventVaultId?: number]
  close: [eventVaultId?: number]
}>()

const { d, t } = useTranslation()

const headers = computed<ITableHeader<EventTableRow>[]>(() => [
  { label: t("host_dashboard.table.title"), value: "title" },
  { label: t("host_dashboard.table.product_type"), value: "productKey" },
  { label: t("host_dashboard.table.status"), value: "status" },
  { label: t("host_dashboard.table.template"), value: "templateName" },
  { label: t("host_dashboard.table.guests"), value: "totalGuests" },
  { label: t("host_dashboard.table.albums"), value: "totalAlbums" },
  { label: t("host_dashboard.table.images"), value: "totalPics" },
  { label: t("host_dashboard.table.videos"), value: "totalVids" },
  { label: t("host_dashboard.table.messages"), value: "totalMessages" },
  { label: t("host_dashboard.table.event_date"), value: "eventDate" },
  { label: t("host_dashboard.table.close_date"), value: "closeDate" },
  { label: t("host_dashboard.table.actions"), value: "actions" },
])

const data = computed<EventTableRow[]>(() => {
  return (
    props.items.map((item: EventVault) => ({
      id: item.id,
      title: item.title,
      eventDate: item.eventDate,
      closeDate: item.closeDate,
      status: item.status,
      templateName: item.eventTemplate?.name,
      eventGuestMax: item.eventVaultSettings?.eventGuestMax,
      totalGuests: item.totalGuests,
      totalAlbums: item.totalAlbums,
      totalPics: item.totalPics,
      totalVids: item.totalVids,
      totalMessages: item.totalMessages,
      productKey: item.productKey,
      productName: item.productName,
      timezone: item.timezone,
      freemiumMode: item.freemiumMode,
      upgradeUrl: item.upgradeUrl,
      actions: "",
    })) ?? []
  )
})

function onShowExport(id?: number) {
  emit("showExport", id)
}
</script>

<template>
  <Table :headers="headers" :data="data" :pagination="pagination" :show-pagination="Boolean(pagination)" dense @pagination-next="$emit('paginationNext')" @pagination-previous="$emit('paginationPrevious')">
    <template #data-title="{ data: row }">
      <span class="font-bold">{{ row.title }}</span>
    </template>
    <template #data-status="{ data: row }">
      <span class="capitalize"> {{ EventVaultStatus.getLabel(row.status ?? EventVaultStatus.Draft) }}</span>
    </template>
    <template #data-productKey="{ data: row }">
      {{ ProductTypes.getLabel(row.productKey, row.productName) }}
    </template>
    <template #data-eventDate="{ data: row }">
      <span v-if="row.eventDate">{{ row?.timezone ? d(convertDateTimezone(row.eventDate, row.timezone), "short") : d(row.eventDate, "short") }}</span>
    </template>
    <template #data-closeDate="{ data: row }">
      <span v-if="row.closeDate">{{ row?.timezone ? d(convertDateTimezone(row.closeDate, row.timezone), "short") : d(row.closeDate, "short") }}</span>
    </template>
    <template #data-totalGuests="{ data: row }">
      {{ row.totalGuests }} / {{ row.eventGuestMax }}
    </template>
    <template #data-actions="{ data: row }">
      <ActionsMenu :event-vault="row as EventVault" @show-export="onShowExport" />
    </template>
  </Table>
</template>
